import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import QRCode from 'qrcode';
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Button, Card, Col, FormGroup, Input, Row } from 'reactstrap';
import Breadcrumb from '../../components/common/breadcrumb';
import ChassisSelectionModal from '../../components/common/chassis-selection-modal/ChassisSelectionModal';
import api from '../../services/api-configuration';
import { checkWarehouseSelection, createBarcodeImage, WAREHOUSE_PRINT_CONFIG } from '../../utils/LabelServiceV2';
import UKLogo from './uk-black-white.png';
// Barcode scanner hook
const useBarcodeScanner = (onBarcodeScanned) => {
    const [buffer, setBuffer] = useState("");
    const timeoutRef = useRef(null);
    const sonTusZamaniRef = useRef(Date.now());
    const TARAYICI_ESIK = 50; // Tuşlar arası süre (ms) - Daha toleranslı bir değer
    const lastScannedRef = useRef(""); // Son okutulan barkod
    const lastScannedTimeRef = useRef(0); // Son okutma zamanı
    const DEBOUNCE_TIME = 1500; // Aynı barkod için minimum bekleme süresi (ms) - Artırıldı

    useEffect(() => {
        const tusBasildiginda = (e) => {
            // Prevent handling if focus is on an input
            if (e.target.tagName === 'INPUT') return;

            const simdikiZaman = Date.now();
            const zamanFarki = simdikiZaman - sonTusZamaniRef.current;

            // Tuşlar arası süre çok kısaysa muhtemelen tarayıcıdır
            const muhtemelTarayici = zamanFarki < TARAYICI_ESIK;

            if (e.key === "Enter") {
                if (buffer) {
                    // Barkod okuyucudan gelen değer her zaman 17 karakter olmalı
                    // Ancak düşük kaliteli cihazlarda eksik karakter olabilir, 16 karakteri de kabul edelim
                    if (muhtemelTarayici && (buffer.length === 17 || buffer.length === 16)) {
                        // Aynı barkod için debounce kontrolü
                        const timeSinceLastScan = simdikiZaman - lastScannedTimeRef.current;
                        if (buffer === lastScannedRef.current && timeSinceLastScan < DEBOUNCE_TIME) {
                            console.log("Duplicate scan prevented", buffer);
                            e.preventDefault();
                            setBuffer("");
                            return;
                        }

                        // Yeni okutma bilgilerini kaydet
                        lastScannedRef.current = buffer;
                        lastScannedTimeRef.current = simdikiZaman;

                        // 16 karakterli şase numaralarını 17 karaktere tamamla (gerekirse)
                        const processedBuffer = buffer.length === 16 ? buffer + "0" : buffer;

                        onBarcodeScanned(processedBuffer);
                        setBuffer("");
                        e.preventDefault(); // Form gönderimini engelle
                    }
                }
            } else if (e.key && e.key.length === 1) {
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                }

                // Muhtemel tarayıcı girişi ise buffer'a ekle
                if (muhtemelTarayici || buffer.length > 0) { // İlk karakter sonrası daha toleranslı ol
                    setBuffer(prev => prev + e.key);
                }

                timeoutRef.current = setTimeout(() => {
                    setBuffer("");
                }, 200); // Daha uzun bir timeout süresi
            }

            sonTusZamaniRef.current = simdikiZaman;
        };

        window.addEventListener("keydown", tusBasildiginda);
        return () => {
            window.removeEventListener("keydown", tusBasildiginda);
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [buffer, onBarcodeScanned]);
};

const EtiketYazdir = () => {
    const [chassis, setChassis] = useState("");
    const [isProcessing, setIsProcessing] = useState(false);
    const [selectedWarehouse, setSelectedWarehouse] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [chassisList, setChassisList] = useState([]);
    const inputRef = useRef(null);
    const processingRef = useRef(false); // API isteği işleniyor mu kontrolü
    const lastProcessedChassisRef = useRef(""); // Son işlenen şase
    const lastProcessTimeRef = useRef(0); // Son işlem zamanı

    useEffect(() => {
        // Check warehouse selection on component mount
        const checkWarehouse = async () => {
            const storedWarehouseId = localStorage.getItem("warehouse");
            const storedWarehouseName = localStorage.getItem("warehouseName");

            if (storedWarehouseId && storedWarehouseName) {
                setSelectedWarehouse({
                    id: Buffer.from(storedWarehouseId, 'base64').toString('ascii'),
                    warehouse_name: storedWarehouseName
                });
            } else {
                const selectedWarehouse = await checkWarehouseSelection();
                if (selectedWarehouse) {
                    setSelectedWarehouse(selectedWarehouse);
                }
            }
        };

        checkWarehouse();
    }, []);

    // Generate QR code
    const generateQR = async (text) => {
        try {
            const options = {
                margin: 0,
                width: 200,
                height: 200,
                color: {
                    dark: '#000000',
                    light: '#ffffff'
                }
            };
            return await QRCode.toDataURL(text, options);
        } catch (err) {
            console.error('QR Code generation error:', err);
            return null;
        }
    };

    // Fetch PDI data
    const fetchPDIData = async (chassisNo) => {
        try {
            const response = await api.post('/getVehicleOrderInfo', { chassis_no: chassisNo.trim() });
            if (response.status === 200) {
                return {
                    success: true,
                    data: {
                        chassis_no: response.data.chassis_no || "",
                        dealer: (response.data.shipping_address === "-/" ? "" : (response.data.shipping_address || "").toLocaleUpperCase("tr-TR")),
                        brand: response.data.brand || "",
                        model: response.data.model || "",
                        groupage_no: response.data.chassis_no || "",
                        pdi_date: response.data.pdi_exit_date
                            ? new Date(response.data.pdi_exit_date).toLocaleDateString('en-US', {
                                weekday: 'long',
                                month: 'long',
                                day: 'numeric',
                                year: 'numeric',
                            })
                            : "",
                        warehouse: response.data.warehouse || "",
                        adress: response.data.address_code || ""
                    }
                };
            }
            throw new Error('API response was not ok');
        } catch (error) {
            console.error('Error fetching PDI data:', error);
            toast.error('Veri çekilirken bir hata oluştu');
            return { success: false, error };
        }
    };

    // Generate and send PDF
    const generatePDFAndSend = async (container, warehouseId, processedValue) => {
        try {
            if (!warehouseId) {
                toast.error('Depo seçimi yapılmadan etiket yazdırılamaz');
                return { success: false };
            }

            const warehouseConfig = WAREHOUSE_PRINT_CONFIG[warehouseId];
            if (!warehouseConfig || !warehouseConfig.hasPrint) {
                toast.error('Seçili depoda yazdırma özelliği bulunmamaktadır');
                return { success: false };
            }

            // Ensure container has white background
            container.style.backgroundColor = '#ffffff';
            container.style.position = 'fixed';
            container.style.top = '0';
            container.style.left = '0';
            container.style.zIndex = '-9999';

            // Wait for images to load
            await Promise.all(
                Array.from(container.querySelectorAll('img')).map(
                    img => img.complete ? Promise.resolve() : new Promise(resolve => img.onload = resolve)
                )
            );

            const canvas = await html2canvas(container, {
                scale: 2,
                useCORS: true,
                allowTaint: true,
                logging: false,
                width: 357.165,
                height: 470.866,
                backgroundColor: '#ffffff',
                imageTimeout: 0,
                removeContainer: true,
                foreignObjectRendering: true,
                letterRendering: true,
                onclone: (clonedDoc) => {
                    const clonedContainer = clonedDoc.querySelector('div');
                    if (clonedContainer) {
                        clonedContainer.style.backgroundColor = '#ffffff';
                        Array.from(clonedContainer.querySelectorAll('*')).forEach(el => {
                            if (getComputedStyle(el).backgroundColor === 'rgba(0, 0, 0, 0)') {
                                el.style.backgroundColor = '#ffffff';
                            }
                        });
                    }
                }
            });

            // Convert canvas to JPEG with white background
            const canvasBlob = await new Promise(resolve => {
                const tempCanvas = document.createElement('canvas');
                tempCanvas.width = canvas.width;
                tempCanvas.height = canvas.height;
                const ctx = tempCanvas.getContext('2d');
                ctx.fillStyle = '#ffffff';
                ctx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
                ctx.drawImage(canvas, 0, 0);
                tempCanvas.toBlob(resolve, 'image/jpeg', 1.0);
            });

            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: [95, 125],
                compress: true,
                putOnlyUsedFonts: true,
                precision: 2,
                hotfixes: ['px_scaling']
            });

            const arrayBuffer = await canvasBlob.arrayBuffer();
            const uint8Array = new Uint8Array(arrayBuffer);

            pdf.addImage(uint8Array, 'JPEG', 0, 0, 95, 125, undefined, 'FAST');

            const filename = `pdi-${processedValue}-${Date.now()}.pdf`;
            const pdfBlob = pdf.output('blob', { compress: true });

            const formData = new FormData();
            formData.append('file', pdfBlob, filename);

            const uploadResponse = await api.post(warehouseConfig.uploadEndpoint, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                maxBodyLength: Infinity,
                maxContentLength: Infinity,
                timeout: 30000,
                decompress: false,
                responseType: 'json'
            });
            if (uploadResponse.status === 200 && uploadResponse.data?.url) {
                const printResponse = await api.post(warehouseConfig.printEndpoint, {
                    FilePath: uploadResponse.data.url,
                    WarehouseId: warehouseId
                }, {
                    timeout: 10000
                });

                if (printResponse.status === 200) {
                    toast.success('Etiket başarıyla yazdırıldı');
                    return { success: true };
                }
                throw new Error('Failed to send print request');
            }
            throw new Error('Failed to upload PDF or URL not received');
        } catch (error) {
            console.error('Error generating or sending PDF:', error);
            toast.error('Etiket yazdırılırken bir hata oluştu');
            return { success: false, error };
        }
    };

    // Handle pattern search for chassis numbers less than 17 characters
    const handleChassisPatternSearch = async (value) => {
        // Add validation for minimum 8 characters
        if (value.trim().length < 8) {
            toast.error('En az 8 karakter giriniz');
            return;
        }

        try {
            const response = await api.post("/getVehiclesByChassisPattern", {
                chassis_pattern: value.trim().toUpperCase()
            });

            if (response.data.success) {
                if (response.data.count === 0) {
                    toast.warning('Şase bulunamadı');
                    return;
                }
                if (response.data.count === 1) {
                    // If only one chassis found, use it directly
                    setChassis(response.data.data[0]);
                    handlePrint(response.data.data[0]);
                } else if (response.data.count > 1) {
                    // If multiple chassis found, show selection modal
                    setChassisList(response.data.data);
                    setIsModalOpen(true);
                }
            }
        } catch (error) {
            console.error("Error searching chassis pattern:", error);
            toast.error("Şase arama sırasında bir hata oluştu");
        }
    };

    // Handle chassis selection from modal
    const handleChassisSelect = (selectedChassis) => {
        setIsModalOpen(false);
        setChassis(selectedChassis);
        // Çift istek kontrolü ekle
        if (!processingRef.current) {
            handlePrint(selectedChassis);
        }
    };

    // Modify handlePrint to include pattern search
    const handlePrint = async (chassisNo = chassis) => {
        if (isProcessing) return;

        // Eğer zaten işleniyor ise, çift isteği engelle
        if (processingRef.current) {
            console.log("Request already in progress, preventing duplicate call");
            return;
        }

        // Aynı şase için kısa süre içinde tekrar istek kontrolü (500ms)
        const now = Date.now();
        const timeSinceLastProcess = now - lastProcessTimeRef.current;
        if (chassisNo === lastProcessedChassisRef.current && timeSinceLastProcess < 500) {
            console.log("Preventing duplicate API call for the same chassis", chassisNo);
            return;
        }

        if (!chassisNo.trim()) {
            toast.warning('Lütfen şase numarası giriniz');
            return;
        }

        const trimmedValue = chassisNo.trim().toUpperCase();

        // Şase numarası 17 karakter olmalı, ancak 16 karakteri de kabul edelim
        // 16 karakterli şase numaralarını 17 karaktere tamamla
        let processedValue = trimmedValue;
        if (trimmedValue.length === 16) {
            processedValue = trimmedValue + "0"; // 16 karakterli şase numarasını tamamla
            console.log("16 karakterli şase numarası 17 karaktere tamamlandı:", processedValue);
        }

        // Always do pattern search if length is less than 16
        if (processedValue.length < 16) {
            await handleChassisPatternSearch(processedValue);
            return;
        }

        if (!selectedWarehouse) {
            toast.error('Lütfen önce depo seçimi yapınız');
            const warehouse = await checkWarehouseSelection();
            if (warehouse) {
                setSelectedWarehouse(warehouse);
            }
            return;
        }

        // İşlem başladı, kilidi aktifleştir
        processingRef.current = true;
        lastProcessedChassisRef.current = processedValue;
        lastProcessTimeRef.current = now;

        try {
            setIsProcessing(true);

            const pdiResponse = await fetchPDIData(processedValue);
            if (!pdiResponse.success) {
                processingRef.current = false; // Kilidi kaldır
                return;
            }

            const { data } = pdiResponse;
            const [qr_image, barcodeBase64] = await Promise.all([
                generateQR(data.groupage_no),
                createBarcodeImage(data.groupage_no)
            ]);

            if (!qr_image || !barcodeBase64) {
                toast.error('QR kod veya barkod oluşturulamadı');
                processingRef.current = false; // Kilidi kaldır
                return;
            }

            // Create container and generate PDF
            const container = document.createElement('div');
            container.innerHTML = `
            <div style="width: 95mm; height: 125mm; font-family: sans-serif; padding: 2mm 6mm 0mm 3mm; background: white;">
                <div style="padding: 0; height: 100%; display: flex; flex-direction: column;">
                    <div style="display: flex; align-items: center; height: 31mm; gap: 2mm;">
                        <div style="height: 70%;">
                            <img alt="UK Logo" src=${UKLogo} style="height: 100%; display: block;">
                        </div>
                        <div style="width:100%">
                            <div style="font-size: 28px; font-weight: 700; text-align: center; width: 100%; border: 0.5mm solid #000; padding: 2mm; margin-bottom: 2mm; background: white;">PDI</div>
                            <div style="font-size: 16px; font-weight: 700; padding-left: 2mm;">${data.groupage_no}</div>
                        </div>
                    </div>
                    <div style="flex: 1; border-top: 0.5mm solid #000; display: grid; grid-template-columns: 18mm 1fr; gap: 0mm; padding-top: 2mm; max-height: 90mm; overflow: hidden;">
                        <div style="height: 100%; display: grid; grid-template-rows: auto 1fr;">
                            <span style="font-size: 3.5mm; font-weight: 700; padding: 0 3mm">BAYİ :</span>
                            <div style="height: 80mm; padding: 2mm 0">
                                <img alt="Barkod" src="${barcodeBase64}" style="height: 100%; width: 100%; object-fit: contain; max-height: 72mm; display: block;">
                            </div>
                        </div>
                        <div style="display: flex; flex-direction: column; gap: 2mm;">
                            <p style="min-height: 5mm; font-size:2.9mm; font-weight: 700; margin: 0;">${data.dealer || ""}</p>
                            <div style="display: flex; justify-content: space-between; align-items: start; gap: 2mm; padding-left: 2mm;">
                                <div style="border: 0.5mm solid #000; padding: 2mm; width: 70%; font-weight: 700; background: white;">
                                    <small style="font-size: 10px; font-weight: 700; display: block;">${data.brand}</small>
                                    <small style="font-size: 10px; font-weight: 700; display: block; margin-top:1mm">${data.model}</small>
                                </div>
                                <div style="width: 25%;">
                                    <img alt="QR" src="${qr_image}" style="width: 100%; display: block;">
                                </div>
                            </div>
                            <div style="padding-left:2mm">
                                <div style="display:flex;justify-content:space-between;border-bottom:0.5mm solid #000;padding:2mm">
                                    <small style="font-size: 10px; font-weight: 700; display: block;">PDI Tarih</small>
                                    <small style="font-size: 10px; font-weight: 700; display: block;">PDI Bölgesi</small>
                                </div>
                                <div style="display:flex;justify-content:space-between;padding:2mm; padding-inline:0;">
                                    <small style="font-size: 10px; font-weight: 700; display: block;">${data.pdi_date}</small>
                                    <small style="font-size: 10px; font-weight: 700; display: block;">${data.warehouse}</small>
                                </div>
                                <div style="font-size: 50px; font-weight: bold; text-align: left; margin-top: 3mm;">${data.adress}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        `;
            document.body.appendChild(container);

            const result = await generatePDFAndSend(container, selectedWarehouse.id, processedValue);
            document.body.removeChild(container);

            if (result.success) {
                setChassis("");
            }
        } catch (error) {
            console.error('Error in print process:', error);
            toast.error('İşlem sırasında bir hata oluştu');
        } finally {
            setIsProcessing(false);
            // İşlem tamamlandı, kilidi kaldır
            setTimeout(() => {
                processingRef.current = false;
            }, 300); // Ekstra güvenlik için kısa bir gecikme

            // Input alanına otomatik odaklan
            if (inputRef.current) {
                inputRef.current.focus();
            }
        }
    };

    // Handle barcode scanner input
    const handleBarcodeScanned = (scannedValue) => {
        setChassis(scannedValue);
        if (!isProcessing && !processingRef.current) {
            handlePrint(scannedValue);
        }
    };

    // Set up barcode scanner
    useBarcodeScanner(handleBarcodeScanned);

    // Handle manual input change
    const handleManualInputChange = (e) => {
        setChassis(e.target.value);
    };

    // Handle manual input enter key
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !isProcessing && !processingRef.current) {
            handlePrint();
        }
    };

    return (
        <>
            <Breadcrumb current="Etiket Yazdır" />
            <Card className="p-2 px-4">
                {/* Warehouse display */}
                {selectedWarehouse && (
                    <div style={{
                        padding: '0px',
                        marginBottom: '20px',
                        borderBottom: '1px solid #eee',
                        display: 'flex',
                        justifyContent: 'flex-end'
                    }}>
                        <small style={{
                            fontWeight: '500',
                            color: '#444',
                        }}>
                            {selectedWarehouse.warehouse_name}
                            {!WAREHOUSE_PRINT_CONFIG[selectedWarehouse.id]?.hasPrint &&
                                <span style={{ color: '#dc3545', marginLeft: '5px' }}>(Yazıcı Yok)</span>
                            }
                        </small>
                    </div>
                )}
                <Row>
                    <Col md="6">
                        <FormGroup className="row">
                            <label className="col-md-2">
                                <b>Şase No</b>
                            </label>
                            <Input
                                ref={inputRef}
                                type="text"
                                className="col-md-8"
                                placeholder="Şase No Giriniz (min. 8 karakter)"
                                value={chassis}
                                onChange={handleManualInputChange}
                                onKeyDown={handleKeyDown}
                                disabled={isProcessing}
                            />
                        </FormGroup>
                    </Col>
                </Row>
                <hr />
                <Row>
                    <Button
                        color="success"
                        className="ml-2 w-50"
                        onClick={() => handlePrint()}
                        disabled={isProcessing}
                    >
                        {isProcessing ? (
                            <>
                                <i className="fa fa-spinner fa-spin"></i> &nbsp;
                                İşleniyor...
                            </>
                        ) : (
                            <>
                                <i className="fa fa-check"></i> &nbsp;
                                Tamam
                            </>
                        )}
                    </Button>
                </Row>
            </Card>

            <ChassisSelectionModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                chassisList={chassisList}
                onSelect={handleChassisSelect}
            />
        </>
    );
};

export default EtiketYazdir;